import { api, unwrap, type APIResponse } from "./client";

/** Mirrors app/routers/agent.py's WarmTransferOut. */
export type WarmTransferState =
  | "consulting"
  | "conference"
  | "agent_left"
  | "ended";

export interface WarmTransfer {
  state: WarmTransferState;
  destination_id: number;
  destination_name: string;
  /** True once the buyer has actually answered. Merge is refused until then. */
  buyer_up: boolean;
  spool_id: number | null;
  seconds_elapsed: number;
  /** A machine reason ("buyer_no_answer", "customer_gone") or "". */
  message: string;
}

const BASE = "/agent/warm-transfer";

// Every route here is keyed by a seat (extension_id). An agent login carries
// its seat in the token, so `extensionId` can be omitted and the backend
// defaults to it. An OPERATOR on the shared login (typed-extension sign-in)
// has no seat in their token — they must say which seat, exactly as
// getCurrentCall/agentHeartbeat already do, or the backend 422s
// 'extension_id is required' and every warm-transfer control fails (beads
// u0np). `undefined` is dropped by axios (no key / no query param), so the
// agent path is unchanged.
export async function startWarmTransfer(
  transferDestinationId: number,
  extensionId?: number,
): Promise<WarmTransfer> {
  const resp = await api.post<APIResponse<WarmTransfer>>(BASE, {
    transfer_destination_id: transferDestinationId,
    extension_id: extensionId,
  });
  return unwrap(resp.data);
}

/**
 * `null` is the normal idle answer, so this must not throw on it — same reason
 * `getCurrentCall` handles its own nullable unwrap (a throwing poll sits
 * permanently in React Query's error state).
 */
export async function getWarmTransfer(
  extensionId?: number,
): Promise<WarmTransfer | null> {
  const resp = await api.get<APIResponse<WarmTransfer | null>>(BASE, {
    params: { extension_id: extensionId },
  });
  if (!resp.data.success) throw new Error(resp.data.error || "Request failed");
  return resp.data.data;
}

export async function mergeWarmTransfer(extensionId?: number): Promise<WarmTransfer> {
  return unwrap(
    (await api.post<APIResponse<WarmTransfer>>(`${BASE}/merge`, undefined, {
      params: { extension_id: extensionId },
    })).data,
  );
}

export async function cancelWarmTransfer(extensionId?: number): Promise<WarmTransfer> {
  return unwrap(
    (await api.post<APIResponse<WarmTransfer>>(`${BASE}/cancel`, undefined, {
      params: { extension_id: extensionId },
    })).data,
  );
}

export async function leaveWarmTransfer(extensionId?: number): Promise<WarmTransfer> {
  return unwrap(
    (await api.post<APIResponse<WarmTransfer>>(`${BASE}/leave`, undefined, {
      params: { extension_id: extensionId },
    })).data,
  );
}
